import { useMemo } from "react";
import { useFetchFeatures } from "../lib/useFetchFeatures";
import { useGeographyLevels } from "./useGeographyLevels";

/**
 * Flattens the Data Catalog's own taxonomy (the same /api/metrics + /api/config/filters that
 * DataCatalogModal already uses via useFetchFeatures/FeatureTypeConfig, plus geography levels
 * from useGeographyLevels) into one searchable list of terms for the search bar's command
 * palette.
 *
 * A metric is one term (its `display` name). A filter contributes one term PER value (e.g. the
 * "gender" filter contributes "Male" and "Female" as separate terms, category = the filter's own
 * `display`) — this is what makes "Females" / "Gender" match the spec's row-anatomy example
 * exactly, rather than indexing "Gender" itself as a single term.
 *
 * `siblings` on each term lists the other values in the same set (other filter values for a
 * filter value, other metrics in the same category for a metric, other geography levels for a
 * geography level) — originally for the token chip's now-removed chevron-swap dropdown, still
 * used by TokenizedSearchInput to tell a resolved term from an unresolved one and, for
 * geography levels specifically, to populate ADD TO YOUR SEARCH directly (see that file).
 */
export function useTermIndex() {
  const { features: metrics, isLoading: isLoadingMetrics } = useFetchFeatures("/api/metrics");
  const { features: filters, isLoading: isLoadingFilters } = useFetchFeatures(
    "/api/config/filters"
  );
  const { geographyLevels, isLoading: isLoadingGeographyLevels } = useGeographyLevels();

  const termIndex = useMemo(() => {
    const terms = [...geographyLevels];

    // "age" comes back from BOTH /api/metrics and /api/config/filters (CategoricalFeature is
    // dual-purpose at the backend) -- excluded here since the metrics-sourced one has no config
    // panel wiring; the filters-sourced "Age" below (feature.type: "age") is the real one.
    const dedupedMetrics = metrics.filter((feature) => feature.name !== "age");

    dedupedMetrics.forEach((feature) => {
      terms.push({
        id: `metric:${feature.name}`,
        display: feature.display || feature.name,
        category: feature.category,
        sourceType: "metric",
        feature,
        // `id` matches the sibling's own real term entry's `metric:{name}` convention -- without
        // it, every sibling row rendered by CommandPalette (keyed on `term.id`) collides on the
        // same `undefined` React key.
        siblings: dedupedMetrics
          .filter((other) => other.category === feature.category && other.name !== feature.name)
          .map((other) => ({
            id: `metric:${other.name}`,
            value: other.name,
            display: other.display || other.name,
            feature: other,
          })),
      });
    });

    filters.forEach((feature) => {
      const values = feature.values || [];
      const displayValues = feature.display_values || [];

      values.forEach((value, idx) => {
        terms.push({
          id: `filter:${feature.name}:${value}`,
          display: displayValues[idx] ?? value,
          category: feature.display || feature.category,
          sourceType: "filterValue",
          feature,
          value,
          // Same reasoning as the metric siblings above -- `id` matches the sibling's own real
          // term entry's convention, avoiding an `undefined` React key collision.
          siblings: values
            .map((v, i) => ({
              id: `filter:${feature.name}:${v}`,
              value: v,
              display: displayValues[i] ?? v,
            }))
            .filter((sibling) => sibling.value !== value),
        });
      });

      // Age is also exposed as a single atomic term (committed as "Age", configured via
      // BandSelector) alongside its normal per-value expansion above. `feature.type: "age"` is a
      // synthetic marker (real filter features carry no `type`) that needsBandConfig reads, same
      // pattern as a continuous metric's real `feature.type`.
      if (feature.name === "age") {
        terms.push({
          id: `filter:${feature.name}`,
          display: feature.display,
          category: "Years of Age",
          sourceType: "filterValue",
          feature: { ...feature, type: "age" },
          siblings: [],
        });
      }
    });

    return terms;
  }, [metrics, filters, geographyLevels]);

  return {
    termIndex,
    isLoading: isLoadingMetrics || isLoadingFilters || isLoadingGeographyLevels,
  };
}
